import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import PageHero from "../components/sections/PageHero";
import StaggerContainer from "../components/motion/StaggerContainer";
import TiltCard from "../components/motion/TiltCard";
import ScrollReveal from "../components/motion/ScrollReveal";
import { useCountries, useLocations } from "../hooks/useCMSData";
import WaveDivider from "../components/graphics/WaveDivider";

const regionalOverviews: Record<string, string> = {
  us: "The United States is our largest market, where we power everything from Silicon Valley SaaS platforms to New York finance portals.",
  uk: "A hub for European finance and operations. We deliver GDPR-compliant, high-security systems for businesses across the UK.",
  uae: "The center of rapid growth. We build fast, premium scalable technology for real estate, government, and commercial sectors.",
  europe: "Across the continent, we act as a secure, high-quality development partner for scale-ups and established enterprises.",
  india: "Our home ground. We build high-volume, robust, and cost-efficient intelligent systems for the world's fastest-growing market.",
};

export default function Locations() {
  const { data: countries } = useCountries();
  const { data: allLocations } = useLocations();

  return (
    <>
      <SEOHead
        title="Global Locations | Obsidian Web Labs"
        description="Obsidian Web Labs serves clients globally. Explore our regional hubs and service areas across the US, UK, Europe, UAE, and India."
        canonicalUrl="https://obsidianweblabs.com/locations"
      />

      <PageHero
        label="REGIONS WE SERVE"
        title="Global Reach."
        titleAccent="Local Excellence."
        subtitle="Wherever you operate, we bring enterprise technology and intelligent automation to your doorstep."
      />

      <section style={{ padding: "120px 40px", background: "#050505" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <ScrollReveal>
            <div
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(36px, 4.5vw, 56px)",
                color: "#F8F4EC",
                marginBottom: 60,
              }}
            >
              Regions we serve
            </div>
          </ScrollReveal>

          <StaggerContainer
            stagger={0.15}
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 32 }}
          >
            {countries.map((country) => {
              const cities = allLocations.filter((l) => l.countrySlug === country.slug);
              return (
                <div key={country.slug}>
                  <Link to={`/${country.slug}`} style={{ textDecoration: "none" }}>
                    <TiltCard
                      style={{
                        background: "#0E0E0E",
                        border: "1px solid rgba(212,168,67,0.18)",
                        borderRadius: 3,
                        padding: 32,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: "'Playfair Display', serif",
                          fontSize: 32,
                          color: "#F8F4EC",
                          marginBottom: 12,
                        }}
                      >
                        {country.name}
                      </div>
                      <div
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontWeight: 300,
                          fontSize: 15,
                          color: "#8B8378",
                          marginBottom: 24,
                          lineHeight: 1.6,
                        }}
                      >
                        {regionalOverviews[country.slug] || `Serving businesses across ${country.name}.`}
                      </div>

                      <div style={{ borderTop: "0.5px solid rgba(212,168,67,0.15)", paddingTop: 20, marginTop: "auto" }}>
                        <div
                          style={{
                            fontFamily: "'Barlow Condensed', sans-serif",
                            fontWeight: 700,
                            fontSize: 12,
                            letterSpacing: "0.2em",
                            color: "#D4A843",
                            textTransform: "uppercase",
                            marginBottom: 16,
                          }}
                        >
                          Major Markets
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                          {cities.slice(0, 6).map((city) => (
                            <Link
                              key={city.slug}
                              to={`/${country.slug}/${city.slug}`}
                              style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontWeight: 300,
                                fontSize: 13,
                                color: "#B8B2A8",
                                padding: "6px 12px",
                                background: "rgba(248,244,236,0.03)",
                                border: "1px solid rgba(248,244,236,0.08)",
                                borderRadius: 4,
                                textDecoration: "none",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = "#F8F4EC";
                                e.currentTarget.style.borderColor = "rgba(212,168,67,0.4)";
                                e.currentTarget.style.background = "rgba(212,168,67,0.05)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#B8B2A8";
                                e.currentTarget.style.borderColor = "rgba(248,244,236,0.08)";
                                e.currentTarget.style.background = "rgba(248,244,236,0.03)";
                              }}
                            >
                              {city.city}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </TiltCard>
                  </Link>
                </div>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      <WaveDivider variant="diagonal" dark />
    </>
  );
}
